import { useState } from 'react'
import { api, formatDate } from '../api'
import { useAuth } from '../AuthContext'
import { useCasquette } from '../contexts/casquette'
import { useActionsPortail } from '../contexts/navigation'
import RinaContextCard from '../components/RinaContextCard'
import PageHub from '../components/PageHub'
import ModuleCard, { useModulesPortail, SkeletonGrille, isEnabled, MODULE_FACETTES } from '../components/ModuleCard'
import { MVP } from '../mvp'
import { avecAlpha } from '../lib/couleurs'

// Codes de lieux deja relies a un parcours dans le portail.
// MVP (S62) : Paniers n'est routable que si MVP.PANIERS — sinon la tuile reste
// « Bientot », quel que soit l'etat BO ou l'acces anticipe du compte.
const ROUTED_CODES = new Set(['MARCHE', ...(MVP.PANIERS ? ['TOURNEE'] : [])])

/**
 * Explorer — casquette CLIENT (S52) : grille des lieux, puis liste des
 * evenements de marche (ecran selecteur, prototype v5 §5 ter) — le detail
 * d'un marche est le module plein ecran /marche/:id.
 * La facette producteur a SA page : PageExplorerProducteur (/producteur/explore).
 * La sous-vue « evenements » est un etat LOCAL : quitter l'onglet demonte la
 * page et remet naturellement la grille (fil d'Ariane conserve tant qu'on
 * reste sur la page — sortir d'un evenement garde la liste).
 */
export default function PageExplorer() {
  const { user } = useAuth()
  const { isProducer } = useCasquette()
  const { openMarche, ouvrirPaniers } = useActionsPortail()

  const modules = useModulesPortail()
  const [vue, setVue] = useState('modules') // 'modules' | 'events'
  const [events, setEvents] = useState([])
  const [loadingEvents, setLoadingEvents] = useState(false)

  const tc = 'text-ink'
  const ts = 'text-ink/60'
  // Casquettes de l'utilisateur connecte : client par defaut, + producteur si org validee.
  const userRoles = user ? ['client', ...(isProducer ? ['producteur'] : [])] : []

  const openEventsList = () => {
    setVue('events')
    // On affiche TOUJOURS l'ecran liste des marches, meme s'il n'y en a qu'un
    // (le client choisit son marche), fidele au parcours « selecteur » du prototype.
    if (events.length > 0) return
    setLoadingEvents(true)
    api.getMarches()
      .then(d => setEvents(Array.isArray(d) ? d : []))
      .catch(() => setEvents([]))
      .finally(() => setLoadingEvents(false))
  }

  // Sous-vue « evenements » : le retour revient a la grille des lieux (le
  // retour vit dans le header — DESIGN-GUIDE §11.8, parametre de PageHub).
  const retour = vue !== 'modules'
    ? { label: 'Explorer', onClick: () => setVue('modules') }
    : null

  return (
    <PageHub retour={retour}>
      <RinaContextCard message="Besoin d'aide pour trouver un produit ? Je connais tous les stands !" />

      {vue === 'events' ? (
        loadingEvents ? (
          <div className="space-y-3">
            <div className={`rounded-2xl h-28 animate-pulse bg-ink/5`} />
            <div className={`rounded-2xl h-28 animate-pulse bg-ink/5`} />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center pt-8 animate-fade-up">
            <span className="text-4xl block mb-2">📭</span>
            <p className={`text-sm font-semibold ${tc}`}>Aucun marché à venir</p>
            <p className={`text-xs mt-1 ${ts}`}>Reviens bientôt pour découvrir les prochains rendez-vous</p>
          </div>
        ) : (
          <>
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary mb-3 mx-0.5">
              Marchés disponibles ({events.length})
            </p>
            <div className="flex flex-col gap-3">
              {events.map((e, i) => {
                const teal = 'rgb(var(--c-teal))'
                const horaire = e.heureDebut && e.heureFin ? `${e.heureDebut} – ${e.heureFin}` : e.heureDebut
                return (
                  <button
                    key={e.id}
                    onClick={() => openMarche(e.id)}
                    style={{ borderLeft: `4px solid ${teal}`, animationDelay: `${i * 0.05}s` }}
                    className="text-left rounded-[24px] p-4 animate-fade-up active:scale-[0.98] transition-transform panneau-verre"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className="w-[52px] h-[52px] rounded-[16px] flex items-center justify-center text-[25px] flex-shrink-0"
                        style={{ background: `linear-gradient(135deg, ${teal}, #0d6b5f)`, boxShadow: `0 8px 18px ${avecAlpha(teal, 0.25)}` }}
                      >{e.typeConfig?.icone || '🎪'}</span>
                      <div className="flex-1 min-w-0">
                        <p className={`font-serif text-[19px] truncate ${tc}`}>{e.nom || e.lieu || 'Évènement'}</p>
                        {e.lieu && <p className={`text-xs mt-0.5 truncate ${ts}`}>📍 {e.lieu}</p>}
                      </div>
                      <span className="text-base font-bold text-primary flex-shrink-0">›</span>
                    </div>
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      <span className="text-[11.5px] font-bold text-ink px-3 py-1.5 rounded-full" style={{ background: 'rgba(7,62,61,0.06)' }}>
                        📅 {formatDate(e.date)}
                      </span>
                      {horaire && (
                        <span className="text-[11.5px] font-bold text-primary px-3 py-1.5 rounded-full" style={{ background: 'rgba(244,137,83,0.12)' }}>
                          🕖 {horaire}
                        </span>
                      )}
                      {e.participantsCount > 0 && (
                        <span className={`text-[11px] ml-auto ${ts}`}>{e.participantsCount} producteur{e.participantsCount > 1 ? 's' : ''}</span>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          </>
        )
      ) : modules === null ? (
        <SkeletonGrille />
      ) : (
        // Grille des lieux — pas de titre : la carte RINA fait office d'en-tete
        // (DESIGN-SYSTEM.md §4, evite le double en-tete).
        <div className="grid grid-cols-2 gap-3 auto-rows-fr">
          {modules.map((m, i) => {
            const en = isEnabled(m, user)
            // Routage des lieux relies ; les autres (Jaden, Lavwa...) restent en « Bientot ».
            const onClick = en && m.code === 'MARCHE' ? openEventsList
              : en && m.code === 'TOURNEE' && MVP.PANIERS ? ouvrirPaniers
              : undefined
            return (
              <ModuleCard
                key={m.code}
                module={m}
                enabled={en && ROUTED_CODES.has(m.code)}
                onClick={onClick}
                animationDelay={`${i * 0.05}s`}
                facetteRoles={(MODULE_FACETTES[m.code] || []).filter((r) => userRoles.includes(r))}
              />
            )
          })}
        </div>
      )}
    </PageHub>
  )
}
